import API from "../../../utils/apiUtils";
import {getPathParam, serialize} from "../../../utils/converter";
import {COMPANY} from "../../../api/endpoints";

const HEADERS_SEGMENT = "headers";
const HEADER_SEGMENT = "header";
const DOCUMENT_SEGMENT = "document";
const CONNECTION_HEADER_SEGMENT = "connection_header";

const buildHeadersEndpoint = ({companyId, type}) => {
    const segments = [COMPANY, String(companyId), HEADERS_SEGMENT, type];
    return getPathParam(segments);
};

const buildHeaderEndpoint = ({companyId, type, documentId = null}) => {
    const segments = [COMPANY, String(companyId), HEADER_SEGMENT, type];
    if (documentId) {
        segments.push(documentId);
    }
    return getPathParam(segments);
};

const buildConnectionHeadersEndpoint = ({companyId, type}) => {
    const segments = [COMPANY, String(companyId), CONNECTION_HEADER_SEGMENT, type];
    return getPathParam(segments);
};

const buildConnectionHeaderEndpoint = ({companyId, type = null, documentId = null}) => {
    const segments = [COMPANY, String(companyId), CONNECTION_HEADER_SEGMENT];
    if (type) {
        segments.push(type);
    }
    if (documentId) {
        segments.push(String(documentId));
    }
    return getPathParam(segments);
};

const buildConnectionHeaderBodiesEndpoint = ({companyId, documentId}) => {
    const segments = [COMPANY, String(companyId), CONNECTION_HEADER_SEGMENT, String(documentId), "bodies"];
    return getPathParam(segments);
};

const buildConnectionHeaderBodyEndpoint = ({companyId, documentId}) => {
    const segments = [COMPANY, String(companyId), CONNECTION_HEADER_SEGMENT, String(documentId), "body"];
    return getPathParam(segments);
};

const buildConnectionHeaderBodyEventsEndpoint = ({companyId, documentId, bodyId}) => {
    const base = buildConnectionHeaderBodyEndpoint({companyId, documentId});
    return `${base}/${String(bodyId)}/events`;
};

const buildConnectionHeaderBodyEventEndpoint = ({companyId, documentId, bodyId}) => {
    const base = buildConnectionHeaderBodyEndpoint({companyId, documentId});
    return `${base}/${String(bodyId)}/event`;
};

const buildConnectionHeaderBodyEventItemEndpoint = ({companyId, documentId, bodyId, eventId}) => {
    const base = buildConnectionHeaderBodyEventEndpoint({companyId, documentId, bodyId});
    return `${base}/${String(eventId)}`;
};

const buildDocumentBodyEndpoint = ({companyId, documentId}) => {
    const segments = [COMPANY, String(companyId), DOCUMENT_SEGMENT, String(documentId), "body"];
    return getPathParam(segments);
};

const buildDocumentRequestEventsEndpoint = ({companyId, documentId}) => {
    const segments = [COMPANY, String(companyId), DOCUMENT_SEGMENT, String(documentId), "request_events"];
    return getPathParam(segments);
};

const buildDocumentListPriceEndpoint = ({companyId, type, documentId}) => {
    const base = buildHeaderEndpoint({companyId, type, documentId});
    return `${base}/list_price`;
};

const buildDocumentBodyItemEndpoint = ({companyId, documentId, bodyId}) => {
    const base = buildDocumentBodyEndpoint({companyId, documentId});
    return `${base}/${bodyId}`;
};

const buildDocumentBodyPositionEndpoint = ({companyId, documentId}) => {
    const base = buildDocumentBodyEndpoint({companyId, documentId});
    return `${base}/position`;
};

const buildDocumentBodyEventEndpoint = ({companyId, documentId, bodyId}) => {
    const base = buildDocumentBodyItemEndpoint({companyId, documentId, bodyId});
    return `${base}/event`;
};

const buildDocumentBodyEventItemEndpoint = ({companyId, documentId, bodyId, eventId}) => {
    const base = buildDocumentBodyEventEndpoint({companyId, documentId, bodyId});
    return `${base}/${eventId}`;
};

const buildDocumentBodyConfirmedQuantityEndpoint = ({companyId, documentId}) => {
    const base = buildDocumentBodyEndpoint({companyId, documentId});
    return `${base}/confirm_qty`;
};

export const fetchDocumentCollection = ({companyId, type, params = {}, data = {}, signal}) => {
    if (!companyId) {
        throw new Error("Missing companyId to fetch documents");
    }
    if (!type) {
        throw new Error("Missing document type to fetch documents");
    }

    const endpoint = buildHeadersEndpoint({companyId, type});
    const query = Object.keys(params ?? {}).length > 0 ? serialize(params) : "";
    return API.post(`${endpoint}${query}`, data, signal ? {signal} : undefined);
};

export const fetchConnectionDocumentCollection = ({companyId, type, params = {}, data = {}, signal}) => {
    if (!companyId) {
        throw new Error("Missing companyId to fetch connection documents");
    }
    if (!type) {
        throw new Error("Missing document type to fetch connection documents");
    }

    const endpoint = buildConnectionHeadersEndpoint({companyId, type});
    const query = Object.keys(params ?? {}).length > 0 ? serialize(params) : "";
    return API.post(`${endpoint}${query}`, data, signal ? {signal} : undefined);
};

export const fetchConnectionDocumentDetail = ({companyId, type, documentId, params = {}, signal}) => {
    if (!companyId || !type || !documentId) {
        throw new Error("Missing parameters to fetch connection document detail");
    }

    const endpoint = buildConnectionHeaderEndpoint({companyId, type, documentId});
    const query = Object.keys(params ?? {}).length > 0 ? serialize(params) : "";
    return API.get(`${endpoint}${query}`, signal ? {signal} : undefined);
};

export const fetchDocumentDetail = ({companyId, type, documentId, params = {}, signal}) => {
    if (!companyId || !type || !documentId) {
        throw new Error("Missing parameters to fetch document detail");
    }

    const endpoint = buildHeaderEndpoint({companyId, type, documentId});
    const query = Object.keys(params ?? {}).length > 0 ? serialize(params) : "";
    return API.get(`${endpoint}${query}`, signal ? {signal} : undefined);
};

export const createDocument = ({companyId, type, data}) => {
    if (!companyId || !type) {
        throw new Error("Missing parameters to create document");
    }

    const endpoint = buildHeaderEndpoint({companyId, type});
    return API.post(endpoint, data ?? {});
};

export const updateDocument = ({companyId, type, documentId, data}) => {
    if (!companyId || !type || !documentId) {
        throw new Error("Missing parameters to update document");
    }

    const endpoint = buildHeaderEndpoint({companyId, type, documentId});
    return API.put(endpoint, data ?? {});
};

export const cloneDocument = ({companyId, type, documentId, data}) => {
    if (!companyId || !type || !documentId) {
        throw new Error("Missing parameters to clone document");
    }

    const endpoint = `${buildHeaderEndpoint({companyId, type, documentId})}/clone`;
    return API.put(endpoint, data ?? {});
};

export const exportDocument = ({companyId, type, documentId, data}) => {
    if (!companyId || !type || !documentId) {
        throw new Error("Missing parameters to export document");
    }

    const endpoint = `${buildHeaderEndpoint({companyId, type, documentId})}/export`;
    return API.put(endpoint, data ?? {});
};

export const deleteDocument = ({companyId, type, documentId}) => {
    if (!companyId || !type || !documentId) {
        throw new Error("Missing parameters to delete document");
    }

    const endpoint = buildHeaderEndpoint({companyId, type, documentId});
    return API.delete(endpoint);
};

export const fetchDocumentBody = ({companyId, documentId, params = {}, signal}) => {
    if (!companyId || !documentId) {
        throw new Error("Missing parameters to fetch document body");
    }

    const endpoint = buildDocumentBodyEndpoint({companyId, documentId});
    const query = Object.keys(params ?? {}).length > 0 ? serialize(params) : "";
    return API.get(`${endpoint}${query}`, signal ? {signal} : undefined);
};

export const fetchDocumentRequestEvents = ({companyId, documentId, params = {}, signal}) => {
    if (!companyId || !documentId) {
        throw new Error("Missing parameters to fetch document request events");
    }

    const endpoint = buildDocumentRequestEventsEndpoint({companyId, documentId});
    const query = Object.keys(params ?? {}).length > 0 ? serialize(params) : "";
    return API.get(`${endpoint}${query}`, signal ? {signal} : undefined);
};

export const fetchConnectionDocumentBody = ({companyId, documentId, params = {}, signal}) => {
    if (!companyId || !documentId) {
        throw new Error("Missing parameters to fetch connection document body");
    }

    const endpoint = buildConnectionHeaderBodiesEndpoint({companyId, documentId});
    const query = Object.keys(params ?? {}).length > 0 ? serialize(params) : "";
    return API.get(`${endpoint}${query}`, signal ? {signal} : undefined);
};

export const fetchConnectionDocumentBodyEvents = ({companyId, documentId, bodyId, params = {}, signal}) => {
    if (!companyId || !documentId || !bodyId) {
        throw new Error("Missing parameters to fetch connection document body events");
    }

    const endpoint = buildConnectionHeaderBodyEventsEndpoint({companyId, documentId, bodyId});
    const query = Object.keys(params ?? {}).length > 0 ? serialize(params) : "";
    return API.get(`${endpoint}${query}`, signal ? {signal} : undefined);
};

export const requestConnectionDocumentBodyQuantity = ({companyId, documentId, bodyId, quantity, type = null}) => {
    if (!companyId || !documentId || !bodyId) {
        throw new Error("Missing parameters to request document body quantity");
    }
    if (quantity === null || quantity === undefined) {
        throw new Error("Missing quantity to request document body quantity");
    }

    const endpoint = `${buildConnectionHeaderBodyEndpoint({companyId, documentId})}/${bodyId}/request_quantity`;
    const payload = {quantity};
    if (type) {
        payload.type = type;
    }
    return API.put(endpoint, payload);
};

export const deleteConnectionDocumentBodyEvent = ({companyId, documentId, bodyId, eventId}) => {
    if (!companyId || !documentId || !bodyId || !eventId) {
        throw new Error("Missing parameters to delete connection document body event");
    }

    const endpoint = buildConnectionHeaderBodyEventItemEndpoint({companyId, documentId, bodyId, eventId});
    return API.delete(endpoint);
};

export const createDocumentBodyRow = ({companyId, documentId, data}) => {
    if (!companyId || !documentId) {
        throw new Error("Missing parameters to create document body row");
    }

    const endpoint = buildDocumentBodyEndpoint({companyId, documentId});
    return API.post(endpoint, data ?? {});
};

export const insertDocumentBodyRows = ({companyId, documentId, bodies}) => {
    if (!companyId || !documentId || !Array.isArray(bodies) || bodies.length === 0) {
        throw new Error("Missing parameters to insert document body rows");
    }

    const endpoint = `${buildDocumentBodyEndpoint({companyId, documentId})}/insert`;
    return API.put(endpoint, {bodies});
};

export const cloneDocumentBodyRow = ({companyId, documentId, bodyId}) => {
    if (!companyId || !documentId || !bodyId) {
        throw new Error("Missing parameters to clone document body row");
    }

    const endpoint = `${buildDocumentBodyItemEndpoint({companyId, documentId, bodyId})}/clone`;
    return API.put(endpoint, {});
};

export const cloneDocumentBodyRows = ({companyId, documentId, ids}) => {
    if (!companyId || !documentId || !Array.isArray(ids) || ids.length === 0) {
        throw new Error("Missing parameters to clone document body rows");
    }

    const endpoint = `${buildDocumentBodyEndpoint({companyId, documentId})}/clone`;
    return API.put(endpoint, {ids});
};

export const updateDocumentBodyRow = ({companyId, documentId, bodyId, data}) => {
    if (!companyId || !documentId || !bodyId) {
        throw new Error("Missing parameters to update document body row");
    }

    const endpoint = buildDocumentBodyItemEndpoint({companyId, documentId, bodyId});
    return API.put(endpoint, data ?? {});
};

export const deleteDocumentBodyRows = ({companyId, documentId, ids}) => {
    if (!companyId || !documentId || !Array.isArray(ids) || ids.length === 0) {
        throw new Error("Missing parameters to delete document body rows");
    }

    const endpoint = `${buildDocumentBodyEndpoint({companyId, documentId})}/delete`;
    return API.put(endpoint, {ids});
};

export const hideDocumentBodyRows = ({companyId, documentId, ids}) => {
    if (!companyId || !documentId || !Array.isArray(ids) || ids.length === 0) {
        throw new Error("Missing parameters to hide document body rows");
    }

    const endpoint = `${buildDocumentBodyEndpoint({companyId, documentId})}/hide`;
    return API.put(endpoint, {ids});
};

export const unhideDocumentBodyRows = ({companyId, documentId, ids}) => {
    if (!companyId || !documentId || !Array.isArray(ids) || ids.length === 0) {
        throw new Error("Missing parameters to unhide document body rows");
    }

    const endpoint = `${buildDocumentBodyEndpoint({companyId, documentId})}/un_hide`;
    return API.put(endpoint, {ids});
};

export const updateDocumentBodyPositions = ({companyId, documentId, ids}) => {
    if (!companyId || !documentId || !Array.isArray(ids) || ids.length === 0) {
        throw new Error("Missing parameters to update document body positions");
    }

    const endpoint = buildDocumentBodyPositionEndpoint({companyId, documentId});
    return API.put(endpoint, {ids});
};

export const fetchDocumentBodyEvents = ({companyId, documentId, bodyId, params = {}, signal}) => {
    if (!companyId || !documentId || !bodyId) {
        throw new Error("Missing parameters to fetch document body events");
    }

    const endpoint = buildDocumentBodyEventEndpoint({companyId, documentId, bodyId});
    const query = Object.keys(params ?? {}).length > 0 ? serialize(params) : "";
    return API.get(`${endpoint}${query}`, signal ? {signal} : undefined);
};

export const createDocumentBodyEvent = ({companyId, documentId, bodyId, data}) => {
    if (!companyId || !documentId || !bodyId) {
        throw new Error("Missing parameters to create document body event");
    }

    const endpoint = buildDocumentBodyEventEndpoint({companyId, documentId, bodyId});
    return API.post(endpoint, data ?? {});
};

export const updateDocumentBodyEvent = ({companyId, documentId, bodyId, eventId, data}) => {
    if (!companyId || !documentId || !bodyId || !eventId) {
        throw new Error("Missing parameters to update document body event");
    }

    const endpoint = buildDocumentBodyEventItemEndpoint({companyId, documentId, bodyId, eventId});
    return API.put(endpoint, data ?? {});
};

export const deleteDocumentBodyEvent = ({companyId, documentId, bodyId, eventId}) => {
    if (!companyId || !documentId || !bodyId || !eventId) {
        throw new Error("Missing parameters to delete document body event");
    }

    const endpoint = buildDocumentBodyEventItemEndpoint({companyId, documentId, bodyId, eventId});
    return API.delete(endpoint);
};

export const confirmDocumentBodyRows = ({companyId, documentId, ids}) => {
    if (!companyId || !documentId || !Array.isArray(ids) || ids.length === 0) {
        throw new Error("Missing parameters to confirm document body rows");
    }

    const endpoint = buildDocumentBodyConfirmedQuantityEndpoint({companyId, documentId});
    return API.put(endpoint, {ids: ids});
};

export const updateDocumentBodyPurchasePrices = ({companyId, type, documentId, data}) => {
    if (!companyId || !type || !documentId) {
        throw new Error("Missing parameters to update document purchase prices");
    }

    const endpoint = buildDocumentListPriceEndpoint({companyId, type, documentId});
    return API.put(endpoint, data ?? {});
};
